import { z } from 'zod';
import { ChannelId } from './common.js';

/**
 * RARITY IS DATA, NOT A UNION TYPE.
 *
 * The predecessors declared it as a compile-time union in FOUR places —
 * `VinylColorRarity`, `AssetRarity`, `CoatRarity`, `ItemRarity` — re-declared
 * verbatim in the frontend and re-hardcoded in roughly ten UI colour tables.
 * Adding a tier meant a two-file type edit plus N tables, and L54 is the
 * lesson about exactly that: *"each of which silently kept the old ladder when
 * a tier was added."*
 *
 * There was also no way for a streamer to have their own tiers at all. A
 * platform serving a hundred communities cannot ship one ladder: a card game
 * wants Common through Secret Rare, and a shooter wants Common through
 * Legendary, and neither is wrong.
 *
 * So a tier is a document. Nothing in this repository may hardcode a ladder,
 * and `check-boundaries` enforces it.
 */

/**
 * The stable identity. Stamped on every copy minted at this tier, and
 * therefore never changed — the label is what changes when somebody renames
 * "Epic" to "Mythic", and every existing copy follows because it stores the
 * key rather than the label.
 */
export const RarityKey = z.string().regex(
  /^[a-z][a-z0-9_]{0,30}$/,
  'A rarity key is lower-case letters, digits and underscores, starting with a letter.'
);

export const RarityTier = z.object({
  channel_id: ChannelId,
  key: RarityKey,
  label: z.string().min(1).max(40),
  /** Hex. The one visual property every surface needs and none should invent. */
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'A colour is #rrggbb.'),
  /** A short glyph or emoji. Deliberately not an image: a tier badge renders in a list. */
  icon: z.string().max(8).default(''),
  /**
   * Where it sits in the ladder, least to most rare.
   *
   * Explicit rather than inferred from creation order, because a streamer
   * inserting "Uncommon" between two existing tiers should not have to delete
   * and recreate everything above it.
   */
  order: z.number().int().min(0).max(999),
  /**
   * Retired tiers stop being offered but never stop resolving.
   *
   * A copy stamped `epic` must render as Epic forever, so a tier is retired
   * and never deleted — the same rule as instances (L9). Deleting one would
   * strand every copy that carries it, which is how their audio rarity came
   * to be recovered by substring-matching the document id.
   */
  retired: z.boolean().default(false),
});

export const RarityTierInput = RarityTier.omit({ channel_id: true, retired: true });

/**
 * The ladder a new channel starts with.
 *
 * SEEDED, not hardcoded — it is written into the channel's own documents at
 * creation and is theirs to rename, recolour, reorder or retire afterwards.
 * Nothing reads this constant at runtime; it exists only to save a streamer
 * from an empty screen.
 */
export const STARTER_RARITY_TIERS: readonly z.infer<typeof RarityTierInput>[] = [
  { key: 'common', label: 'Common', color: '#9aa0ab', icon: '', order: 10 },
  { key: 'uncommon', label: 'Uncommon', color: '#4ba55c', icon: '', order: 20 },
  { key: 'rare', label: 'Rare', color: '#3b82f6', icon: '', order: 30 },
  { key: 'epic', label: 'Epic', color: '#a855f7', icon: '', order: 40 },
  { key: 'legendary', label: 'Legendary', color: '#f59e0b', icon: '', order: 50 },
];

export type RarityTier = z.infer<typeof RarityTier>;
export type RarityTierInput = z.infer<typeof RarityTierInput>;
